// 使用函数作用域变量，避免全局状态污染
import microApp from '@micro-zoe/micro-app'

import {
  isEqualObject,
  getTargetURL,
  getMFEPathname,
  getIframeURL,
  getSubAppURL,
  setItem,
  getItem,
  removeItem,
  getOrigin,
  getPathname,
  isRouteInRoutes,
  PORTAL_SEARCH_HISTORY_LIST,
  PORTAL_ROUTER_TAG_LIST
} from './utils'

const MFE_MAP_KEY = 'MFE_CODE_ORIGIN_MAP'
let mfeMap = {}
let registeredRoutes = []
let mfeGrayPermission = false

/**
 * 判断路由是否注册
 * @param {*} path 路由路径
 * @returns {*} 是否注册
 */
const isPortalRoute = path => {
  const pathname = getPathname(path)?.toUpperCase()
  if (!pathname) {
    return false
  }

  return isRouteInRoutes(pathname, registeredRoutes)
}

/**
 * 重置菜单路径
 * @param {*} menuItem 菜单项
 * @param {*} parentMenuPath 父菜单origin
 * @param {*} parentMenuCode 父菜单code
 */
const resetMenuItemForMFE = (menuItem, parentMenuPath, parentMenuCode) => {
  menuItem.menuCode = menuItem.menuCode || parentMenuCode
  if (
    menuItem.menuPath &&
    mfeMap[menuItem.menuCode?.toUpperCase() || parentMenuCode?.toUpperCase()]
  ) {
    if (menuItem.menuPath.startsWith('http')) {
      const origin = getOrigin(menuItem.menuPath)
      const targetCode = getMenuCodeWithOrigin(origin)
      if (targetCode) {
        menuItem.menuCode = targetCode || menuItem.menuCode
        menuItem.menuPath = getMFEMenuPath(menuItem)
        menuItem.origin = origin
      }
    } else {
      menuItem.origin = parentMenuPath
      menuItem.menuPath =
        `/${menuItem.menuCode}/`.toLowerCase() +
        (menuItem.menuPath.startsWith('/')
          ? menuItem.menuPath.slice(1)
          : menuItem.menuPath)
    }
  }
}

/**
 * 获取菜单路径
 * @param {*} item 菜单项
 * @param {*} parentMenuPath 父菜单menuPath
 * @returns {*} 菜单路径
 */
const combineMenuPath = (item, parentMenuPath) => {
  let { menuPath } = item
  if (menuPath.startsWith('http') || !menuPath || !parentMenuPath) {
    return menuPath
  }
  menuPath = menuPath.replace(/(?<!:)(?<!:)\/\/+/g, '/')
  let slash =
    parentMenuPath.endsWith('/') ||
    menuPath.startsWith('/#') ||
    menuPath.startsWith('/')
      ? ''
      : '/'
  const href = parentMenuPath + slash
  if (href.endsWith('/') && menuPath.startsWith('/')) {
    menuPath = menuPath.slice(1)
  }
  return href + menuPath
}

export const DEFAULT_MFE_ORIGIN_MAP = {
  // psc: process.env.VUE_APP_PLM_HOST
}

const getDefaultMFEOrigin = origin => {
  return Object.keys(DEFAULT_MFE_ORIGIN_MAP).find(
    key =>
      origin === DEFAULT_MFE_ORIGIN_MAP[key] ||
      origin.startsWith(DEFAULT_MFE_ORIGIN_MAP[key])
  )
}

/**
 * 重置菜单列表
 * @param {*} list 菜单列表
 * @param {*} parentMenuCode 父菜单code
 * @param {*} parentMenuPath 父菜单origin
 * @param {*} parentIntegrationMode 父菜单集成模式
 * @param {*} parentGrayCode 父菜单灰度模式
 * @returns {*} 重置后的菜单列表
 */
const resetMenuList = (
  list,
  parentMenuCode,
  parentMenuPath,
  parentIntegrationMode,
  parentGrayCode
) => {
  return list.map(item => {
    const grayCode = parentGrayCode === undefined ? item.isGray : parentGrayCode
    const isMFEMode =
      Object.keys(mfeMap).length > 0 &&
      (parentIntegrationMode === 'mfe' || item.integrationMode === 'mfe') &&
      (grayCode === 0 || (grayCode === 1 && mfeGrayPermission))
    if (item.parentId !== 0) {
      if (item.menuPath) {
        // 子菜单的menuPath需要根据父菜单的menuPath进行拼接
        if (!isMFEMode) {
          item.menuPath = combineMenuPath(item, parentMenuPath)
        } else {
          resetMenuItemForMFE(item, parentMenuPath, parentMenuCode)
        }
      } else {
        item.menuPath = parentMenuPath
      }
    }
    if (item.childrenList?.length) {
      const list = item.menuPath
        ?.split(',')
        .filter(item => /^https?:\/\//i.test(item))
      let currentMenuPath = parentMenuPath || list?.[0] || ''
      let menuCode = parentMenuCode || item.menuCode
      if (isMFEMode && item.parentId === 0 && list?.length > 1) {
        if (currentMenuPath?.startsWith('http')) {
          currentMenuPath = getOrigin(currentMenuPath)
        }
        const code = getDefaultMFEOrigin(currentMenuPath)
        if (code) {
          menuCode = code.toUpperCase()
        }
      }

      item.childrenList = resetMenuList(
        item.childrenList,
        menuCode,
        currentMenuPath,
        parentIntegrationMode || item.integrationMode,
        parentGrayCode || item.isGray
      )
    }
    return item
  })
}

/**
 * 获取mfe菜单路径
 * @param {*} item 菜单项
 * @returns {*} mfe菜单路径
 */
export const getMFEMenuPath = item => {
  if (!item?.menuPath || !item?.menuCode) {
    return item?.menuPath
  }

  try {
    const menuCode = String(item.menuCode).toLowerCase()
    const obj = new URL(item.menuPath)
    const currentPath =
      obj.hash && obj.pathname === '/' ? obj.hash.slice(1) : obj.pathname
    return `/${menuCode}${currentPath}`
  } catch (error) {
    console.warn('Invalid menuPath URL:', item.menuPath)
    return item.menuPath
  }
}

/**
 * 获取菜单code
 * @param {*} origin 菜单origin
 * @returns {*} 菜单code
 */
export const getMenuCodeWithOrigin = origin => {
  const codeList = Object.keys(mfeMap)
  let code = codeList.find(code =>
    mfeMap[code].find(
      item => item === origin || item.includes(origin) || origin.includes(item)
    )
  )
  if (!code) {
    code = getDefaultMFEOrigin(origin)
  }
  return code
}

export const getMFEMap = () => {
  const sessionMFEHostMap = getItem(MFE_MAP_KEY, 'session')
  if (sessionMFEHostMap) {
    return sessionMFEHostMap
  }
  return getItem(MFE_MAP_KEY)
}

/**
 * 获取mfe路由
 * @param {*} routes 路由列表
 * @returns {*} mfe路由列表
 */
export const getMFERoutes = routes => {
  const map = getMFEMap()
  const mfeProjects = map ? Object.keys(map) : []
  registeredRoutes = routes
  return mfeProjects.map(name => {
    const path = `/${name.toLowerCase()}/:pathMatch(.*)*`
    const route = {
      path,
      name
    }
    return {
      ...route,
      component: () => import('@/views/MicroApplication/index.vue')
    }
  })
}

/**
 * 获取菜单路由
 * @param {*} item 菜单项
 * @param {*} menuList 菜单列表
 * @returns {*} 菜单路由
 */
export const getMenuItem = (item, menuList) => {
  if (
    !item ||
    Object.keys(mfeMap).length === 0 ||
    isPortalRoute(item?.menuPath)
  ) {
    return item
  }
  let menuCode = item?.menuCode
  let pathname = ''
  if (!menuCode) {
    if (item?.menuPath?.startsWith('http')) {
      pathname = getPathnameFromUrl(item.menuPath, menuList)
    }
    if (pathname) {
      const list = pathname.split('/').filter(item => item)
      menuCode = list[0]
    }
  }
  if (menuCode && mfeMap[menuCode?.toUpperCase()]) {
    item.menuPath = pathname
    item.basePath = pathname.split('?')[0]
  }
  return item
}

/**
 * 获取查询参数
 * @param {*} pathname 路径
 * @returns {*} 查询参数
 */
export const getQueryPath = pathname => {
  if (!pathname || typeof pathname !== 'string') {
    return { path: '', query: {} }
  }

  const paths = pathname.split(/\?+/)
  const path = paths[0] || ''
  const queryString = paths[1] || ''
  const params = new URLSearchParams(queryString)
  const query = {}

  params.forEach((value, key) => {
    query[key] = value
  })

  return { path, query }
}

/**
 * 获取路径名
 * @param {*} url 路径
 * @param {*} menuList 菜单列表
 * @returns {*} 路径名
 */
export const getPathnameFromUrl = (url, menuList) => {
  if (!url || typeof url !== 'string') {
    return ''
  }

  try {
    let { origin, pathname, hash, search } = new URL(url)
    if (pathname === '/' && hash) {
      pathname = hash.slice(1)
    }
    let code = getMenuCodeWithOrigin(origin)
    if (code) {
      pathname = '/' + code.toLowerCase() + pathname
    } else if (origin === location.origin) {
      if (isPortalRoute(pathname)) {
        return pathname
      }
      const target = findMenuItem(menuList, pathname)
      if (target) {
        if (target?.menuCode) {
          pathname = '/' + target.menuCode.toLowerCase() + pathname
          return pathname + search
        } else {
          code = getMenuCodeWithOrigin(target.menuPath)
          if (code) {
            pathname = '/' + code.toLowerCase() + pathname
          }
        }
      }
    }
    return pathname + search
  } catch (error) {
    console.warn('Invalid URL in getPathnameFromUrl:', url, error)
    return url
  }
}

/**
 * 获取菜单路由
 * @param {*} menuList 菜单列表
 * @param {*} pathname 路径名
 * @returns {*} 菜单路由
 */
export const findMenuItem = (menuList, pathname) => {
  if (!Array.isArray(menuList) || !pathname || typeof pathname !== 'string') {
    return null
  }

  let partialMatch = null

  const dfs = nodes => {
    for (const node of nodes) {
      if (!node?.menuPath) continue

      const menuPath = node.menuPath.toLowerCase().split('?')[0]
      const targetPath = pathname.toLowerCase().split('?')[0]

      // 精确匹配
      if (menuPath === targetPath || menuPath.endsWith(targetPath)) {
        const { childrenList: _, ...menuItem } = node
        return menuItem
      }

      // 模糊匹配
      if (!partialMatch) {
        const targetPathList = targetPath.split('/')
        for (let i = targetPathList.length - 1; i > 1; i--) {
          const path = targetPathList.slice(0, i).join('/')
          if (path && path !== '/' && menuPath.includes(path)) {
            const { childrenList: _, ...menuItem } = node
            partialMatch = menuItem
            break
          }
        }
      }

      // 递归查找子菜单
      const children = node.childrenList
      if (Array.isArray(children)) {
        const found = dfs(children)
        if (found) return found
      }
    }

    return null
  }

  const exact = dfs(menuList)
  return exact || partialMatch
}

/**
 * 重置搜索历史列表
 * @param {*} mfeProjectsAdd 添加的mfe项目
 * @param {*} mfeProjectsRemove 删除的mfe项目
 */
const resetStorageList = (mfeKeys, newMenuList, type) => {
  const storageKey =
    type === 'searchHistory'
      ? PORTAL_SEARCH_HISTORY_LIST
      : PORTAL_ROUTER_TAG_LIST
  const storageType = type === 'searchHistory' ? '' : 'session'
  let data = getItem(storageKey, storageType) || []
  data = data.map(item => {
    const path = item.path || item.menuPath
    if (isPortalRoute(path)) {
      return item
    }
    if (path.startsWith('http')) {
      const code = getMenuCodeWithOrigin(getOrigin(path))
      if (code && mfeKeys.includes(code.toUpperCase())) {
        if (item.path) {
          item.path = getMFEPathname(item.path, code)
        } else if (item.menuPath) {
          item.menuPath = getMFEPathname(item.menuPath, code)
        }
        return item
      }
    } else if (path.startsWith('/')) {
      const code = path.slice(1).split('/')[0]
      if (code && !mfeKeys.includes(code.toUpperCase())) {
        const pathname = path.slice(code.length + 1)
        if (item.path) {
          item.path = getSubAppURL(code, pathname, getMFEMap(), newMenuList)
        } else if (item.menuPath) {
          item.menuPath = getSubAppURL(code, pathname, getMFEMap(), newMenuList)
        }
        return item
      }
    }
    return item
  })
  console.log('storageKey', storageKey, data)
  setItem(storageKey, data, storageType)
}

/**
 * 刷新页面
 * @param {*} mfeKeys mfe项目
 * @param {*} newMenuList 新的菜单列表
 */
const reload = (mfeKeys, newMenuList) => {
  let newURL = ''
  let { origin, pathname, search } = new URL(location.href)
  if (pathname === '/Iframe' || !isPortalRoute(pathname)) {
    if (pathname === '/Iframe' && search.includes('?target')) {
      const childAppUrl = getTargetURL()
      const code = getMenuCodeWithOrigin(getOrigin(childAppUrl))
      if (code && mfeKeys.includes(code.toUpperCase())) {
        newURL = origin + getMFEPathname(childAppUrl, code)
      }
    } else if (pathname.startsWith('/')) {
      const code = pathname.slice(1).split('/')[0]
      if (code && !mfeKeys.includes(code.toUpperCase())) {
        pathname = pathname.slice(code.length + 1)
        newURL = getIframeURL(code, pathname, getMFEMap(), newMenuList) + search
      }
    }
  }
  removeItem(MFE_MAP_KEY, 'session')
  if (newURL) {
    location.href = newURL
  } else {
    location.reload()
  }
}

/**
 * 初始化菜单列表
 * @param {*} menuList 菜单列表
 */
export const initMenuList = menuList => {
  if (!Array.isArray(menuList)) {
    console.error('menuList should be an array')
    return []
  }
  // 重置全局状态，避免状态累积
  mfeMap = {}
  // 开启灰度后需要判断是否有微前端权限
  mfeGrayPermission = !!menuList.find(item => {
    return (
      !item?.menuPath &&
      (!Array.isArray(item.childrenList) || item.childrenList?.length === 0) &&
      item?.menuCode === 'MFE'
    )
  })

  // 过滤掉MFE配置菜单项并处理菜单数据
  const newMenuList = menuList.filter(item => item?.menuCode !== 'MFE')
  newMenuList.forEach(item => {
    const { menuPath, menuCode } = item || {}
    if (item?.parentId === 0 && menuPath && menuCode) {
      // 微前端开启逻辑
      // 1、integrationMode 为 MFE
      // 2、是否需要灰度 isGray 为 1 需要判断是否有 MFE 配置菜单，否则全量开启微前端
      if (
        menuPath.startsWith('http') &&
        item.integrationMode === 'mfe' &&
        ((item.isGray === 1 && mfeGrayPermission) || item.isGray === 0)
      ) {
        try {
          const menuPathList = menuPath.split(',').map(getOrigin)
          if (menuPathList.length > 1) {
            const keys = Object.keys(DEFAULT_MFE_ORIGIN_MAP)
            menuPathList.forEach(origin => {
              keys.forEach((key, idx) => {
                if (
                  DEFAULT_MFE_ORIGIN_MAP[key] === origin ||
                  origin.startsWith(DEFAULT_MFE_ORIGIN_MAP[key])
                ) {
                  mfeMap[key.toUpperCase()] = [origin]
                  menuPathList.splice(idx, 1)
                }
              })
            })
          }
          mfeMap[menuCode.toUpperCase()] = menuPathList
        } catch (error) {
          console.warn('Error processing menuPath:', menuPath, error)
        }
      }
    }
  })

  // 检查微前端项目列表是否发生变化,如果发生变化需要重置搜索列表和routerTags
  const mfeMapOld = getMFEMap() || {}
  if (mfeMapOld && !isEqualObject(mfeMapOld, mfeMap)) {
    const mfeKeys = Object.keys(mfeMap)
    // 微前端配置有改动后需要更新CUPSHE_SEARCH_HISTORY_LIST和routerTags
    resetStorageList(mfeKeys, newMenuList, 'searchHistory')
    resetStorageList(mfeKeys, newMenuList, 'routerTags')
    reload(mfeKeys, newMenuList)
  }

  // 更新存储
  if (Object.keys(mfeMap)?.length) {
    setItem(MFE_MAP_KEY, mfeMap)
  } else {
    removeItem(MFE_MAP_KEY)
  }

  return resetMenuList(newMenuList)
}

const getAppName = name => {
  const appName = decodeURIComponent(
    name.startsWith('/') ? name.slice(1) : name
  )
    .replace(/[=/?&]/g, '-')
    .toLowerCase()
  return appName
}

export const unmountApp = item => {
  if (item.menuPath) {
    const appName = getAppName(item.menuPath)
    setTimeout(() => {
      microApp.unmountApp(appName, { clearAliveState: true })
    }, 0)
  }
}

export const reloadApp = (item, forceReload = false) => {
  if (item.menuPath) {
    const appName = getAppName(item.menuPath)
    const appList = microApp.getAllApps()
    if (appList.includes(appName)) {
      setTimeout(() => {
        microApp.reload(appName, forceReload)
      }, 0)
    }
  }
}

export const unmountAppList = list => {
  list.forEach(item => {
    unmountApp(item)
  })
}

export const throttle = (func, delay) => {
  let lastCall = 0
  return function(...args) {
    const now = new Date().getTime()
    if (now - lastCall < delay) return
    lastCall = now
    return func.apply(this, args)
  }
}
